// US States list
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

export const US_STATE_NAMES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
};

// Format phone number as (XXX) XXX-XXXX
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = digits.slice(0, 10);
  
  // Format based on length
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Validate phone number (must be 10 digits)
export const validatePhoneNumber = (value) => {
  if (!value) return { valid: true, message: '' }; // Optional field
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return { valid: true, message: '' };
  if (digits.length !== 10) {
    return { valid: false, message: 'Phone must be 10 digits' };
  }
  return { valid: true, message: '' };
};

// Format SSN as XXX-XX-XXXX
export const formatSSN = (value) => {
  if (!value) return '';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 9 digits
  const limited = digits.slice(0, 9);
  
  // Format based on length
  if (limited.length === 0) return '';
  if (limited.length <= 3) return limited;
  if (limited.length <= 5) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  return `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`;
};

// Validate SSN (must be 9 digits)
export const validateSSN = (value) => {
  if (!value) return { valid: true, message: '' }; // Optional field
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return { valid: true, message: '' };
  if (digits.length !== 9) {
    return { valid: false, message: 'SSN must be 9 digits' };
  }
  return { valid: true, message: '' };
};

// Format zip code as XXXXX or XXXXX-XXXX
export const formatZipCode = (value) => {
  if (!value) return '';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 9 digits (for extended zip)
  const limited = digits.slice(0, 9);
  
  // Format based on length
  if (limited.length <= 5) return limited;
  return `${limited.slice(0, 5)}-${limited.slice(5)}`;
};

// Validate zip code (5 or 9 digits)
export const validateZipCode = (value) => {
  if (!value) return { valid: true, message: '' }; // Optional field
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return { valid: true, message: '' };
  if (digits.length !== 5 && digits.length !== 9) {
    return { valid: false, message: 'Zip must be 5 or 9 digits' };
  }
  return { valid: true, message: '' };
};

// Validate state (must be valid US state abbreviation)
export const validateState = (value) => {
  if (!value) return { valid: true, message: '' }; // Optional field
  const upper = value.toUpperCase().trim();
  if (upper.length === 0) return { valid: true, message: '' };
  if (!US_STATES.includes(upper)) {
    return { valid: false, message: 'Enter valid US state (e.g., TX)' };
  }
  return { valid: true, message: '' };
};

// Format state to uppercase
export const formatState = (value) => {
  if (!value) return '';
  return value.toUpperCase().slice(0, 2);
};

// Identify field type by name
export const getFieldType = (fieldName) => {
  const name = fieldName.toLowerCase();
  if (name.includes('phone') || name.includes('fax')) return 'phone';
  if (name.includes('ssn')) return 'ssn';
  if (name.includes('zip')) return 'zip';
  if (name === 'state' || name.endsWith('_state')) return 'state';
  return 'text';
};

// Format value based on field type
export const formatFieldValue = (fieldName, value) => {
  const type = getFieldType(fieldName);
  switch (type) {
    case 'phone': return formatPhoneNumber(value);
    case 'ssn': return formatSSN(value);
    case 'zip': return formatZipCode(value);
    case 'state': return formatState(value);
    default: return value;
  }
};

// Validate value based on field type
export const validateFieldValue = (fieldName, value) => {
  const type = getFieldType(fieldName);
  switch (type) {
    case 'phone': return validatePhoneNumber(value);
    case 'ssn': return validateSSN(value);
    case 'zip': return validateZipCode(value);
    case 'state': return validateState(value);
    default: return { valid: true, message: '' };
  }
};

// Validate all form fields and return errors
export const validateFormData = (formData) => {
  const errors = {};
  
  Object.entries(formData).forEach(([key, value]) => {
    if (typeof value === 'string') {
      const validation = validateFieldValue(key, value);
      if (!validation.valid) {
        errors[key] = validation.message;
      }
    }
  });
  
  return errors;
};
